import { forwardRef, useCallback, useImperativeHandle, useState } from 'react'

import {
  GoogleMap,
  GoogleMapProps,
  LoadScript,
  Marker,
} from '@react-google-maps/api'

import { GOOGLE_API_KEY } from 'hooks/usePlacesAutocomplete'
import { CareHospital } from 'interfaces/types'

import { GMAP_DEFAULT_ZOOM, GMAP_OPTIONS } from './clinicLocator.helpers'

export type ClinicLocatorMapProps = Pick<
  GoogleMapProps,
  'zoom' | 'options' | 'onLoad' | 'center' | 'onCenterChanged' | 'children'
> & {
  markerItems?: CareHospital[]
  markerItemProps?: { onClick?: (clinic: CareHospital) => void }
}
export const ClinicLocatorMap = forwardRef<
  google.maps.Map,
  ClinicLocatorMapProps
>((props, ref) => {
  const {
    options = GMAP_OPTIONS,
    zoom = GMAP_DEFAULT_ZOOM,
    onLoad: onLoadProps,
    center,
    onCenterChanged,
    markerItems,
    markerItemProps: { onClick } = {},
    children,
  } = props

  const [mapRef, setMapRef] = useState<google.maps.Map>()

  const onLoad = useCallback(
    (map: google.maps.Map) => {
      setMapRef(map)
      onLoadProps?.(map)
    },
    [onLoadProps],
  )

  useImperativeHandle(ref, () => mapRef as google.maps.Map, [mapRef])

  return (
    <LoadScript googleMapsApiKey={GOOGLE_API_KEY}>
      <GoogleMap
        id="clinicLocator--map"
        zoom={zoom}
        options={options}
        onLoad={onLoad}
        onCenterChanged={onCenterChanged}
        mapContainerClassName="g-map"
        center={center}
      >
        {markerItems?.map((clinic) => (
          <Marker
            key={clinic.id}
            position={{
              lat: Number(clinic.latitude),
              lng: Number(clinic.longitude),
            }}
            onClick={() => onClick?.(clinic)}
          />
        ))}
        {children}
      </GoogleMap>
    </LoadScript>
  )
})
